import React, { useEffect, useRef } from 'react';
import useThemeStore from '$/store/useThemeStore';
import styles from './index.module.scss';

type RGB = [number, number, number];

type ThemeConfig = {
  bg: string;
  base: RGB;
  bright: RGB;
  hover: RGB;
  hoverTint?: number;
  hoverNeutral?: number;
  minAlpha: number;
  alphaScale: number;
};

type Dot = {
  x: number;
  y: number;
  seed: number;
  phase: number;
  speed: number;
  twinklePhase: number;
  twinkleSpeed: number;
  shadePhase: number;
  shadeSpeed: number;
};

type Plate = {
  x: number;
  y: number;
  rx: number;
  ry: number;
  angle: number;
  phaseX: number;
  phaseY: number;
  phaseSize: number;
  driftX: number;
  driftY: number;
  speedX: number;
  speedY: number;
  sizeSpeed: number;
  sizeRange: number;
  seed: number;
  weight: number;
};

type ActivePlate = {
  cx: number;
  cy: number;
  invRx: number;
  invRy: number;
  cos: number;
  sin: number;
  pulse: number;
  seed: number;
  weight: number;
};

const CFG = {
  dot: { size: 2, gap: 24, hoverSize: 3 },
  render: { fps: 30, maxDpr: 2 },
  hover: { radius: 200, tint: 0.24 },
  alpha: {
    breath: 0.055,
    twinkle: 0.08,
    plate: 0.2,
    hover: 0.34,
  },
  themes: {
    dark: {
      bg: '#0a0a0a',
      base: [74, 77, 84] as RGB,
      bright: [146, 149, 156] as RGB,
      hover: [120, 87, 255] as RGB,
      minAlpha: 0.24,
      alphaScale: 1,
    },
    light: {
      bg: '#f0f2f5',
      base: [130, 136, 148] as RGB,
      bright: [96, 104, 120] as RGB,
      hover: [124, 92, 255] as RGB,
      hoverTint: 1,
      hoverNeutral: 0.16,
      minAlpha: 0.18,
      alphaScale: 0.68,
    },
  } satisfies Record<'dark' | 'light', ThemeConfig>,
};

/** 将数值限制在 0 到 1 之间，供透明度和颜色混合复用。 */
function clamp(value: number) {
  return Math.max(0, Math.min(1, value));
}

/** 使用平滑曲线避免点阵明暗变化产生生硬断层。 */
function ease(value: number) {
  return value * value * value * (value * (value * 6 - 15) + 10);
}

/** 基于种子生成稳定伪随机值，让每次重绘保持相同的点阵分布。 */
function rand(seed: number) {
  const value = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return value - Math.floor(value);
}

/** 在相邻随机值之间插值，生成缓慢变化的区域亮度。 */
function noise(seed: number, time: number) {
  const index = Math.floor(time);
  const factor = ease(time - index);
  return rand(seed + index) * (1 - factor) + rand(seed + index + 1) * factor;
}

/** 模拟 smoothstep，用于控制亮区边缘和鼠标悬浮衰减。 */
function smooth(min: number, max: number, value: number) {
  const normal = clamp((value - min) / (max - min));
  return normal * normal * (3 - 2 * normal);
}

/** 混合两个 RGB 颜色，保持 canvas 绘制参数稳定。 */
function mixColor(from: RGB, to: RGB, amount: number): RGB {
  const ratio = clamp(amount);
  return [
    Math.round(from[0] + (to[0] - from[0]) * ratio),
    Math.round(from[1] + (to[1] - from[1]) * ratio),
    Math.round(from[2] + (to[2] - from[2]) * ratio),
  ];
}

/** 转成 canvas fillStyle 需要的 rgba 字符串。 */
function rgba(color: RGB, alpha: number) {
  return `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${alpha})`;
}

/** 渲染首页局部的动态点阵背景。 */
function DotMotionBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = useThemeStore((state) => state.theme);
  const themeKey = theme === 'light' ? 'light' : 'dark';

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = canvas?.parentElement;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !container || !ctx) return undefined;

    let width = 0;
    let height = 0;
    let dpr = 1;
    let dots: Dot[] = [];
    let plates: Plate[] = [];
    let frameId = 0;
    let lastFrame = 0;
    const pointer = {
      x: 0,
      y: 0,
      tx: 0,
      ty: 0,
      active: false,
      strength: 0,
    };
    const colors = CFG.themes[themeKey];

    /** 构建缓慢漂移的亮区板块，复刻 CodePen 中的呼吸区域效果。 */
    const buildPlates = () => {
      const count = Math.max(7, Math.min(13, Math.round((width * height) / 130000)));
      plates = Array.from({ length: count }, (_, index) => {
        const r = (seed: number) => rand(index + seed);
        return {
          x: r(11) * width,
          y: r(23) * height,
          rx: width * (0.13 + r(31) * 0.17),
          ry: height * (0.08 + r(43) * 0.14),
          angle: r(59) * Math.PI,
          phaseX: r(71) * Math.PI * 2,
          phaseY: r(83) * Math.PI * 2,
          phaseSize: r(97) * Math.PI * 2,
          driftX: width * (0.14 + r(109) * 0.14),
          driftY: height * (0.12 + r(127) * 0.14),
          speedX: 0.72 + r(139) * 0.5,
          speedY: 0.64 + r(151) * 0.46,
          sizeSpeed: 0.56 + r(163) * 0.5,
          sizeRange: 0.34 + r(179) * 0.2,
          seed: index * 101.13,
          weight: 0.72 + r(127) * 0.28,
        };
      });
    };

    /** 按当前容器尺寸生成点阵，避免页面滚动内容撑开后背景留空。 */
    const buildDots = () => {
      const cols = Math.ceil(width / CFG.dot.gap) + 2;
      const rows = Math.ceil(height / CFG.dot.gap) + 2;
      const offsetX = (width - (cols - 1) * CFG.dot.gap) / 2;
      const offsetY = (height - (rows - 1) * CFG.dot.gap) / 2;
      dots = [];
      for (let y = 0; y < rows; y += 1) {
        for (let x = 0; x < cols; x += 1) {
          const id = y * cols + x;
          dots.push({
            x: Math.round(offsetX + x * CFG.dot.gap),
            y: Math.round(offsetY + y * CFG.dot.gap),
            seed: id * 19.19,
            phase: rand(id + 3) * Math.PI * 2,
            speed: 0.34 + rand(id + 5) * 0.34,
            twinklePhase: rand(id + 41) * Math.PI * 2,
            twinkleSpeed: 0.96 + rand(id + 53) * 0.92,
            shadePhase: rand(id + 67) * Math.PI * 2,
            shadeSpeed: 0.44 + rand(id + 79) * 0.46,
          });
        }
      }
    };

    /** 根据 canvas 实际展示区域和屏幕 DPR 更新绘制尺寸，避免背景铺到左侧导航栏。 */
    const resize = () => {
      canvas.style.width = '';
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, CFG.render.maxDpr);
      width = Math.max(1, Math.round(rect.width));
      height = Math.max(1, Math.round(container.scrollHeight || rect.height));
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.height = `${height}px`;
      canvas.style.background = colors.bg;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingEnabled = false;
      buildPlates();
      buildDots();
    };

    /** 计算当前时刻每个亮区板块的位置、尺寸和亮度。 */
    const getActivePlates = (time: number): ActivePlate[] => plates.map((plate) => {
      const size = 1 + Math.sin(time * plate.sizeSpeed + plate.phaseSize) * plate.sizeRange;
      return {
        cx: plate.x + Math.sin(time * plate.speedX + plate.phaseX) * plate.driftX,
        cy: plate.y + Math.cos(time * plate.speedY + plate.phaseY) * plate.driftY,
        invRx: 1 / (plate.rx * size),
        invRy: 1 / (plate.ry * (1.08 - (size - 1) * 0.45)),
        cos: Math.cos(plate.angle),
        sin: Math.sin(plate.angle),
        pulse: 0.48 + noise(plate.seed + 211, time * 0.58) * 0.52,
        seed: plate.seed,
        weight: plate.weight,
      };
    });

    /** 计算单个点受所有漂移亮区影响后的亮度。 */
    const getPlateLight = (dot: Dot, activePlates: ActivePlate[]) => {
      let light = 0;
      activePlates.forEach((plate) => {
        const dx = dot.x - plate.cx;
        const dy = dot.y - plate.cy;
        const px = (dx * plate.cos + dy * plate.sin) * plate.invRx;
        const py = (-dx * plate.sin + dy * plate.cos) * plate.invRy;
        const edgeNoise = (rand(dot.seed + plate.seed + 17) - 0.5) * 0.22;
        const body = 1 - smooth(0.12, 1.12, px * px + py * py + edgeNoise);
        light = Math.max(light, body * plate.pulse * plate.weight);
      });
      return light;
    };

    /** 计算鼠标附近点阵的紫色高亮强度。 */
    const getHoverLight = (dot: Dot) => {
      if (pointer.strength <= 0.001) return 0;
      const dx = dot.x - pointer.x;
      const dy = dot.y - pointer.y;
      const distSq = dx * dx + dy * dy;
      const radiusSq = CFG.hover.radius * CFG.hover.radius;
      return distSq >= radiusSq ? 0 : (1 - smooth(0, CFG.hover.radius, Math.sqrt(distSq))) * pointer.strength;
    };

    /** 平滑鼠标位置和悬浮强度，让光圈移动更自然。 */
    const updatePointer = () => {
      pointer.x += (pointer.tx - pointer.x) * 0.38;
      pointer.y += (pointer.ty - pointer.y) * 0.38;
      pointer.strength += ((pointer.active ? 1 : 0) - pointer.strength) * 0.18;
    };

    /** 绘制当前帧，只处理当前视口附近的点，控制长页面性能开销。 */
    const draw = (now = 0) => {
      if (now - lastFrame < 1000 / CFG.render.fps) {
        frameId = window.requestAnimationFrame(draw);
        return;
      }

      lastFrame = now;
      const time = now * 0.001;
      const plateList = getActivePlates(time);
      const rect = canvas.getBoundingClientRect();
      const viewTop = Math.max(0, -rect.top - CFG.dot.gap);
      const viewBottom = Math.min(height, viewTop + window.innerHeight + CFG.dot.gap * 2);

      updatePointer();
      ctx.clearRect(0, viewTop, width, viewBottom - viewTop);

      dots.forEach((dot) => {
        if (dot.y < viewTop || dot.y > viewBottom) return;

        const plate = getPlateLight(dot, plateList);
        const hover = getHoverLight(dot);
        const active = clamp(plate + hover);
        let alpha = colors.minAlpha;
        let colorAmount = plate * 0.5 + hover * (colors.hoverNeutral ?? 0.62);

        if (active > 0.001) {
          const breath = (Math.sin(time * dot.speed + dot.phase) + 1) / 2;
          const field = (Math.sin(time * 0.42 + dot.x * 0.018 - dot.y * 0.014) + 1) / 2;
          const twinkle = smooth(0.64, 1, (Math.sin(time * dot.twinkleSpeed + dot.twinklePhase) + 1) / 2);
          const shade = 0.72 + ((Math.sin(time * dot.shadeSpeed + dot.shadePhase) + 1) / 2) * 0.28;
          alpha += (
            ease(breath) * CFG.alpha.breath
            + field * 0.025
            + twinkle * CFG.alpha.twinkle
            + plate * CFG.alpha.plate
            + hover * CFG.alpha.hover
          ) * shade;
          colorAmount += (twinkle * 0.14 + field * 0.04) * active;
        }

        const neutral = mixColor(colors.base, colors.bright, colorAmount);
        const color = mixColor(neutral, colors.hover, hover * (colors.hoverTint ?? CFG.hover.tint));
        const radius = (CFG.dot.size + (CFG.dot.hoverSize - CFG.dot.size) * hover) / 2;
        ctx.fillStyle = rgba(color, alpha * colors.alphaScale);
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, radius, 0, Math.PI * 2);
        ctx.fill();
      });

      frameId = window.requestAnimationFrame(draw);
    };

    /** 将全局指针坐标转换为当前背景 canvas 内的局部坐标。 */
    const handlePointerMove = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      const inside = event.clientX >= rect.left
        && event.clientX <= rect.right
        && event.clientY >= rect.top
        && event.clientY <= rect.bottom;
      pointer.tx = event.clientX - rect.left;
      pointer.ty = event.clientY - rect.top;
      if (!pointer.active) {
        pointer.x = pointer.tx;
        pointer.y = pointer.ty;
      }
      pointer.active = inside;
    };

    /** 鼠标离开页面或窗口失焦时收起悬浮光圈。 */
    const deactivatePointer = () => {
      pointer.active = false;
    };

    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
    resizeObserver?.observe(container);
    resizeObserver?.observe(canvas);
    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerleave', deactivatePointer);
    window.addEventListener('blur', deactivatePointer);
    frameId = window.requestAnimationFrame(draw);

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerleave', deactivatePointer);
      window.removeEventListener('blur', deactivatePointer);
    };
  }, [themeKey]);

  return <canvas ref={canvasRef} className={styles.dotMotionBackground} aria-hidden="true" />;
}

export default DotMotionBackground;
